import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Lock,
  Plus,
  Trash2,
  Save,
  Upload,
  Download,
  ExternalLink,
  Search,
  LogOut,
  Copy,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "monetiseup_admin_token";

const emptySeller = () => ({
  seller_id: "",
  name: "",
  domain: "",
  seller_type: "PUBLISHER",
  is_confidential: 0,
  is_passthrough: 0,
});

function useToast() {
  const [msg, setMsg] = useState(null);
  const show = useCallback((text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3200);
  }, []);
  const node = msg ? (
    <div
      data-testid="admin-toast"
      className={`fixed top-6 right-6 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl border text-sm ${
        msg.type === "error"
          ? "bg-red-500/10 border-red-500/40 text-red-200"
          : "bg-emerald-500/10 border-emerald-500/40 text-emerald-200"
      }`}
    >
      {msg.type === "error" ? (
        <AlertCircle className="w-4 h-4" />
      ) : (
        <CheckCircle2 className="w-4 h-4" />
      )}
      {msg.text}
    </div>
  ) : null;
  return { show, node };
}

export default function AdminSellers() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const { show, node: toastNode } = useToast();

  // On mount, if token exists, verify it
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (r.ok) setAuthed(true);
        else {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        }
      } catch {
        setAuthed(false);
      }
    })();
  }, [token]);

  if (!authed) {
    return <LoginScreen onSuccess={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} showToast={show} toastNode={toastNode} />;
  }

  return (
    <Dashboard
      token={token}
      onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); }}
      showToast={show}
      toastNode={toastNode}
    />
  );
}

/* ─────────────────────────────────────────────── Login screen */
function LoginScreen({ onSuccess, showToast, toastNode }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: pwd }),
      });
      if (r.ok) {
        onSuccess(pwd);
      } else {
        showToast("Invalid admin token", "error");
      }
    } catch (err) {
      showToast("Network error", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen grid place-items-center px-6 pt-32 pb-20">
      {toastNode}
      <div className="aurora-orb bg-[#0047FF] w-[500px] h-[500px] top-0 left-1/2 -translate-x-1/2 opacity-30" />

      <form
        onSubmit={submit}
        className="glass-strong rounded-3xl p-8 md:p-10 w-full max-w-md relative z-10"
        data-testid="admin-login-form"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0047FF] to-[#00F0FF] grid place-items-center mb-5">
          <Lock className="w-5 h-5 text-white" />
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-bold mb-2">
          Sellers Admin
        </h1>
        <p className="text-white/60 text-sm mb-6">
          Enter your admin token to manage the <code className="text-[#00F0FF]">sellers.json</code> feed.
        </p>

        <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
          Admin token
        </label>
        <input
          data-testid="admin-token-input"
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          autoFocus
          placeholder="paste token"
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#00F0FF] focus:outline-none transition-colors text-white placeholder-white/30"
        />

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || !pwd}
          className="btn-primary w-full justify-center mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Verifying..." : "Sign in"}
        </button>

        <Link
          to="/"
          className="block text-center text-xs text-white/40 hover:text-white/70 mt-6"
        >
          ← Back to site
        </Link>
      </form>
    </main>
  );
}

/* ─────────────────────────────────────────────── Dashboard */
function Dashboard({ token, onLogout, showToast, toastNode }) {
  const [sellers, setSellers] = useState([]);
  const [config, setConfig] = useState({
    contact_email: "",
    contact_address: "",
    version: "1.0",
    identifiers: [],
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(emptySeller());
  const [bulk, setBulk] = useState("");
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState("domains"); // 'domains' | 'rows'
  const [bulkDomains, setBulkDomains] = useState({
    seller_id: "",
    name: "",
    seller_type: "PUBLISHER",
    is_confidential: 0,
    domainsText: "",
  });
  const [bulkRowsText, setBulkRowsText] = useState("");
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        fetch(`${API}/api/admin/sellers`, { headers: authHeaders }),
        fetch(`${API}/api/admin/sellers/config`, { headers: authHeaders }),
      ]);
      if (sRes.ok) {
        const s = await sRes.json();
        setSellers(s.sellers || []);
      }
      if (cRes.ok) {
        const c = await cRes.json();
        setConfig({
          contact_email: c.contact_email || "",
          contact_address: c.contact_address || "",
          version: c.version || "1.0",
          identifiers: c.identifiers || [],
        });
      }
    } catch {
      showToast("Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, showToast]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sellers;
    return sellers.filter(
      (s) =>
        s.seller_id.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        s.domain.toLowerCase().includes(q)
    );
  }, [sellers, search]);

  const addSeller = async (e) => {
    e.preventDefault();
    if (!draft.seller_id || !draft.name || !draft.domain) {
      showToast("Fill seller_id, name and domain", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/sellers`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(draft),
      });
      if (r.ok) {
        setDraft(emptySeller());
        await refresh();
        showToast("Seller added");
      } else {
        const err = await r.json().catch(() => ({}));
        showToast(err.detail || "Failed to add seller", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateInline = async (row, patch) => {
    const updated = { ...row, ...patch };
    try {
      const r = await fetch(`${API}/api/admin/sellers/${row.id}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(updated),
      });
      if (r.ok) {
        setSellers((prev) => prev.map((s) => (s.id === row.id ? updated : s)));
      } else {
        showToast("Update failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    }
  };

  const deleteSeller = async (row) => {
    if (!window.confirm(`Delete ${row.name} (${row.domain})?`)) return;
    try {
      const r = await fetch(`${API}/api/admin/sellers/${row.id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (r.ok) {
        setSellers((prev) => prev.filter((s) => s.id !== row.id));
        showToast("Deleted");
      } else {
        showToast("Delete failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/sellers/config`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(config),
      });
      if (r.ok) showToast("Contact info saved");
      else showToast("Save failed", "error");
    } catch {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const bulkAddDomains = async () => {
    const { seller_id, name, seller_type, is_confidential, domainsText } = bulkDomains;
    if (!seller_id.trim() || !name.trim()) {
      showToast("Provide seller_id and name", "error");
      return;
    }
    const domains = domainsText
      .split(/[\n,]+/)
      .map((d) => d.trim())
      .filter(Boolean);
    if (domains.length === 0) {
      showToast("Add at least one domain", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/sellers/bulk-add-domains`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          seller_id: seller_id.trim(),
          name: name.trim(),
          seller_type,
          is_confidential: Number(is_confidential),
          domains,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setBulkDomains({ ...bulkDomains, domainsText: "" });
        await refresh();
        showToast(`Added ${d.added} — skipped ${d.skipped_duplicates} duplicates`);
      } else {
        showToast("Bulk add failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const importFullPreview = () => {
    try {
      const parsed = JSON.parse(importText);
      const list = Array.isArray(parsed) ? parsed : parsed.sellers || [];
      if (!Array.isArray(list)) throw new Error();
      const seen = new Set();
      let dupes = 0;
      for (const s of list) {
        const k = `${s.seller_id}|${s.domain}`;
        if (seen.has(k)) dupes++;
        else seen.add(k);
      }
      setImportPreview({
        sellers_count: list.length,
        unique_count: seen.size,
        duplicates_in_input: dupes,
        contact_email: parsed.contact_email ?? null,
        contact_address: parsed.contact_address ?? null,
        version: parsed.version ?? null,
        identifiers_count: Array.isArray(parsed.identifiers) ? parsed.identifiers.length : 0,
      });
      setImportResult(null);
    } catch {
      setImportPreview(null);
      showToast("Invalid JSON — please paste a valid sellers.json file", "error");
    }
  };

  const importFull = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch {
      showToast("Invalid JSON", "error");
      return;
    }
    const list = Array.isArray(parsed) ? parsed : parsed.sellers || [];
    if (!Array.isArray(list) || list.length === 0) {
      showToast("The file must contain a non-empty 'sellers' array", "error");
      return;
    }
    if (!window.confirm(`Replace ALL existing sellers with ${list.length} rows and publish to /sellers.json?`)) {
      return;
    }
    setSaving(true);
    setImportResult(null);
    try {
      const body = {
        contact_email: parsed.contact_email ?? null,
        contact_address: parsed.contact_address ?? null,
        version: parsed.version ?? null,
        identifiers: Array.isArray(parsed.identifiers) ? parsed.identifiers : null,
        sellers: list,
      };
      const r = await fetch(`${API}/api/admin/sellers/import-full`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const d = await r.json();
        setImportResult(d);
        setImportText("");
        setImportPreview(null);
        await refresh();
        showToast(`Imported ${d.inserted} sellers + published`);
      } else {
        const err = await r.json().catch(() => ({}));
        showToast(err.detail || "Import failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };


  const bulkAppendRows = async () => {
    const text = bulkRowsText.trim();
    if (!text) return;
    // Parse: try JSON array first, then CSV
    let list = null;
    try {
      const parsed = JSON.parse(text);
      list = Array.isArray(parsed) ? parsed : parsed.sellers || null;
    } catch {
      // CSV: header optional, columns: seller_id, name, domain, [seller_type], [is_confidential]
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      let start = 0;
      const header = lines[0].toLowerCase();
      if (/seller_id|name|domain/.test(header)) start = 1;
      list = [];
      for (let i = start; i < lines.length; i++) {
        const cols = lines[i].split(/[,\t]+/).map((c) => c.trim());
        if (cols.length < 3) continue;
        list.push({
          seller_id: cols[0],
          name: cols[1],
          domain: cols[2],
          seller_type: cols[3] || "PUBLISHER",
          is_confidential: Number(cols[4] || 0),
          is_passthrough: 0,
        });
      }
    }
    if (!Array.isArray(list) || list.length === 0) {
      showToast("No rows found. Provide JSON array or CSV.", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/sellers/bulk-append`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ sellers: list }),
      });
      if (r.ok) {
        const d = await r.json();
        setBulkRowsText("");
        await refresh();
        showToast(`Added ${d.added} — skipped ${d.skipped_duplicates} duplicates`);
      } else {
        showToast("Bulk append failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const bulkReplace = async () => {
    let parsed;
    try {
      parsed = JSON.parse(bulk);
    } catch {
      showToast("Invalid JSON", "error");
      return;
    }
    const list = Array.isArray(parsed) ? parsed : parsed.sellers || [];
    if (!Array.isArray(list) || list.length === 0) {
      showToast("Provide an array of sellers or a full sellers.json", "error");
      return;
    }
    if (!window.confirm(`Replace ALL sellers with ${list.length} entries?`)) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/sellers/bulk-replace`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ sellers: list }),
      });
      if (r.ok) {
        const d = await r.json();
        setBulk("");
        await refresh();
        showToast(`Replaced with ${d.inserted} sellers`);
      } else {
        showToast("Bulk replace failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/sellers/publish`, {
        method: "POST",
        headers: authHeaders,
      });
      if (r.ok) showToast("Published to /sellers.json");
      else showToast("Publish failed", "error");
    } catch {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => showToast("URL copied"));
  };

  const liveApiUrl = `${API}/api/sellers.json`;
  const rootUrl = typeof window !== "undefined" ? `${window.location.origin}/sellers.json` : "/sellers.json";

  return (
    <main className="relative min-h-screen pt-28 pb-20">
      {toastNode}
      <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-40 -left-40 opacity-25" />
      <div className="aurora-orb bg-[#8A2BE2] w-[500px] h-[500px] top-1/2 right-0 opacity-20" />

      <div className="container-x relative z-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-3">
              <Lock className="w-3 h-3 text-[#00F0FF]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/70">
                Admin
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold">
              Sellers <span className="aurora-text">.json</span>
            </h1>
            <p className="text-white/60 text-sm mt-2">
              Manage the IAB Tech Lab sellers.json feed for MonetiseUP.
            </p>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="btn-ghost text-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>

        {/* One-shot: paste full sellers.json + publish */}
        <div
          className="rounded-2xl p-6 md:p-8 mb-6 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(0,71,255,0.18), rgba(0,240,255,0.08))",
            border: "1px solid rgba(0,240,255,0.35)",
          }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-1.5">
                One-Click Publish
              </div>
              <h2 className="text-xl md:text-2xl font-bold">
                Paste full sellers.json → replace + publish
              </h2>
              <p className="text-white/60 text-sm mt-1 max-w-2xl">
                Paste the entire contents of your sellers.json (with{" "}
                <code>contact_email</code>, <code>contact_address</code>,{" "}
                <code>sellers</code>, etc). One click replaces everything in the
                database and writes the static <code>/sellers.json</code> file.
              </p>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await fetch(liveApiUrl);
                  setImportText(JSON.stringify(await r.json(), null, 2));
                  setImportPreview(null);
                  setImportResult(null);
                } catch {
                  showToast("Failed to load current file", "error");
                }
              }}
              className="btn-ghost text-xs"
              data-testid="import-load-current"
            >
              <Download className="w-3.5 h-3.5" />
              Load current file
            </button>
          </div>

          <textarea
            data-testid="import-full-textarea"
            value={importText}
            onChange={(e) => {
              setImportText(e.target.value);
              setImportPreview(null);
              setImportResult(null);
            }}
            rows={10}
            placeholder={`Paste your full sellers.json here, e.g.
{
  "contact_email": "business@responsiq.com",
  "contact_address": "...",
  "version": "1.0",
  "identifiers": [],
  "sellers": [
    { "seller_id": "...", "name": "...", "domain": "...", "seller_type": "PUBLISHER", "is_confidential": 0 }
  ]
}`}
            className="admin-input font-mono text-xs resize-y mt-4"
            style={{ background: "rgba(0,0,0,0.35)" }}
          />

          {importPreview && (
            <div
              data-testid="import-preview"
              className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
            >
              <PreviewChip
                label="Sellers"
                value={`${importPreview.unique_count}${
                  importPreview.duplicates_in_input > 0
                    ? ` (of ${importPreview.sellers_count})`
                    : ""
                }`}
              />
              <PreviewChip
                label="Duplicates in input"
                value={importPreview.duplicates_in_input}
              />
              <PreviewChip
                label="contact_email"
                value={importPreview.contact_email || "(unchanged)"}
              />
              <PreviewChip
                label="version"
                value={importPreview.version || "(unchanged)"}
              />
            </div>
          )}

          {importResult && (
            <div
              data-testid="import-result"
              className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-xs flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              Imported <b>{importResult.inserted}</b> sellers, skipped{" "}
              {importResult.duplicates_skipped} duplicates.{" "}
              {importResult.published
                ? "Static file published."
                : "⚠︎ Static file NOT published — check server logs."}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              onClick={importFullPreview}
              disabled={!importText.trim()}
              data-testid="import-preview-btn"
              className="btn-ghost text-sm disabled:opacity-50"
            >
              Preview
            </button>
            <button
              type="button"
              onClick={importFull}
              disabled={saving || !importText.trim()}
              data-testid="import-publish-btn"
              className="btn-primary text-sm disabled:opacity-50"
              style={{
                boxShadow: "0 0 24px rgba(0,240,255,0.25)",
              }}
            >
              <Upload className="w-4 h-4" />
              {saving ? "Importing..." : "Import & publish"}
            </button>
            <span className="text-[11px] text-white/50 self-center">
              Replaces all existing sellers. Duplicates by (seller_id, domain)
              are de-duplicated automatically.
            </span>
          </div>
        </div>


        {/* Public URL panel */}
        <div className="glass-strong rounded-2xl p-6 mb-6 grid md:grid-cols-2 gap-4">
          <UrlBox
            label="✓ Primary — share this with ad exchanges (IAB standard)"
            url={rootUrl}
            onCopy={() => copyUrl(rootUrl)}
            testId="url-root"
            primary
          />
          <UrlBox
            label="Live source (updates instantly, no publish needed)"
            url={liveApiUrl}
            onCopy={() => copyUrl(liveApiUrl)}
            testId="url-api"
          />
          <div className="md:col-span-2 flex flex-wrap gap-3 pt-2 border-t border-white/5">
            <button
              onClick={publish}
              disabled={saving}
              data-testid="publish-static"
              className="btn-primary text-sm disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Publish to /sellers.json
            </button>
            <a
              href={rootUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              View /sellers.json
            </a>
            <span className="text-xs text-white/40 self-center">
              After edits, click Publish + redeploy to update the root <code>/sellers.json</code> URL. The <code>/api/sellers.json</code> source stays live automatically.
            </span>
          </div>
        </div>

        {/* Contact info */}
        <div className="glass-strong rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Contact info (sellers.json header)</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="contact_email">
              <input
                data-testid="config-email"
                value={config.contact_email}
                onChange={(e) => setConfig({ ...config, contact_email: e.target.value })}
                className="admin-input"
              />
            </Field>
            <Field label="version">
              <input
                data-testid="config-version"
                value={config.version}
                onChange={(e) => setConfig({ ...config, version: e.target.value })}
                className="admin-input"
              />
            </Field>
            <Field label="contact_address" className="md:col-span-3">
              <textarea
                data-testid="config-address"
                value={config.contact_address}
                onChange={(e) => setConfig({ ...config, contact_address: e.target.value })}
                rows={2}
                className="admin-input resize-none"
              />
            </Field>
          </div>
          <button
            onClick={saveConfig}
            disabled={saving}
            data-testid="save-config"
            className="btn-primary text-sm mt-4 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Save contact info
          </button>
        </div>

        {/* Add seller */}
        <div className="glass-strong rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Add seller</h2>
          <form onSubmit={addSeller} className="grid md:grid-cols-6 gap-3 items-end">
            <Field label="seller_id" className="md:col-span-1">
              <input
                data-testid="new-seller-id"
                value={draft.seller_id}
                onChange={(e) => setDraft({ ...draft, seller_id: e.target.value })}
                placeholder="mup-XXXX"
                className="admin-input"
              />
            </Field>
            <Field label="name" className="md:col-span-1">
              <input
                data-testid="new-seller-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="admin-input"
              />
            </Field>
            <Field label="domain" className="md:col-span-1">
              <input
                data-testid="new-seller-domain"
                value={draft.domain}
                onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
                placeholder="example.com"
                className="admin-input"
              />
            </Field>
            <Field label="type" className="md:col-span-1">
              <select
                data-testid="new-seller-type"
                value={draft.seller_type}
                onChange={(e) => setDraft({ ...draft, seller_type: e.target.value })}
                className="admin-input"
              >
                <option>PUBLISHER</option>
                <option>INTERMEDIARY</option>
                <option>BOTH</option>
              </select>
            </Field>
            <Field label="confidential" className="md:col-span-1">
              <select
                value={draft.is_confidential}
                onChange={(e) => setDraft({ ...draft, is_confidential: Number(e.target.value) })}
                className="admin-input"
              >
                <option value={0}>0 (public)</option>
                <option value={1}>1 (confidential)</option>
              </select>
            </Field>
            <button
              type="submit"
              disabled={saving}
              data-testid="add-seller-btn"
              className="btn-primary text-sm justify-center disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </form>
        </div>

        {/* Bulk add */}
        <div className="glass-strong rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-lg font-semibold">Bulk add</h2>
            <div className="inline-flex text-xs rounded-lg border border-white/10 overflow-hidden">
              <button
                type="button"
                onClick={() => setBulkMode("domains")}
                data-testid="bulk-mode-domains"
                className={`px-3 py-1.5 transition-colors ${
                  bulkMode === "domains"
                    ? "bg-[#0047FF]/30 text-white"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Many domains — one seller
              </button>
              <button
                type="button"
                onClick={() => setBulkMode("rows")}
                data-testid="bulk-mode-rows"
                className={`px-3 py-1.5 transition-colors ${
                  bulkMode === "rows"
                    ? "bg-[#0047FF]/30 text-white"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Many sellers (CSV / JSON)
              </button>
            </div>
          </div>

          {bulkMode === "domains" ? (
            <>
              <p className="text-xs text-white/50 mb-3">
                Add many websites under a single seller_id and name. Paste one domain per line (or comma-separated). Existing (seller_id, domain) pairs are skipped.
              </p>
              <div className="grid md:grid-cols-4 gap-3 mb-3">
                <Field label="seller_id">
                  <input
                    data-testid="bulk-domains-sellerid"
                    value={bulkDomains.seller_id}
                    onChange={(e) => setBulkDomains({ ...bulkDomains, seller_id: e.target.value })}
                    placeholder="mup-XXXX"
                    className="admin-input"
                  />
                </Field>
                <Field label="name">
                  <input
                    data-testid="bulk-domains-name"
                    value={bulkDomains.name}
                    onChange={(e) => setBulkDomains({ ...bulkDomains, name: e.target.value })}
                    className="admin-input"
                  />
                </Field>
                <Field label="type">
                  <select
                    data-testid="bulk-domains-type"
                    value={bulkDomains.seller_type}
                    onChange={(e) => setBulkDomains({ ...bulkDomains, seller_type: e.target.value })}
                    className="admin-input"
                  >
                    <option>PUBLISHER</option>
                    <option>INTERMEDIARY</option>
                    <option>BOTH</option>
                  </select>
                </Field>
                <Field label="confidential">
                  <select
                    value={bulkDomains.is_confidential}
                    onChange={(e) => setBulkDomains({ ...bulkDomains, is_confidential: Number(e.target.value) })}
                    className="admin-input"
                  >
                    <option value={0}>0 (public)</option>
                    <option value={1}>1 (confidential)</option>
                  </select>
                </Field>
              </div>
              <textarea
                data-testid="bulk-domains-textarea"
                value={bulkDomains.domainsText}
                onChange={(e) => setBulkDomains({ ...bulkDomains, domainsText: e.target.value })}
                placeholder={"example.com\nanother.com\nblog.example.io"}
                rows={7}
                className="admin-input font-mono text-xs resize-y"
              />
              <button
                onClick={bulkAddDomains}
                disabled={saving}
                data-testid="bulk-add-domains-btn"
                className="btn-primary text-sm mt-3 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add domains
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-white/50 mb-3">
                Paste multiple sellers as JSON array or CSV. CSV columns: <code>seller_id, name, domain, seller_type, is_confidential</code>. Existing (seller_id, domain) pairs are skipped — nothing is deleted.
              </p>
              <textarea
                data-testid="bulk-rows-textarea"
                value={bulkRowsText}
                onChange={(e) => setBulkRowsText(e.target.value)}
                placeholder={`seller_id,name,domain,seller_type,is_confidential\nmup-100,ACME,acme.com,PUBLISHER,0\nmup-100,ACME,acme.net,PUBLISHER,0`}
                rows={7}
                className="admin-input font-mono text-xs resize-y"
              />
              <button
                onClick={bulkAppendRows}
                disabled={saving || !bulkRowsText.trim()}
                data-testid="bulk-append-rows-btn"
                className="btn-primary text-sm mt-3 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add sellers
              </button>
            </>
          )}
        </div>

        {/* Sellers table */}
        <div className="glass-strong rounded-2xl p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold">
              Sellers <span className="text-white/40 font-normal">({sellers.length})</span>
            </h2>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="sellers-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search seller_id, name, domain..."
                className="admin-input pl-10 min-w-[300px]"
              />
            </div>
          </div>

          <div className="overflow-x-auto -mx-2">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
                  <th className="px-3 py-2 font-medium">seller_id</th>
                  <th className="px-3 py-2 font-medium">name</th>
                  <th className="px-3 py-2 font-medium">domain</th>
                  <th className="px-3 py-2 font-medium">type</th>
                  <th className="px-3 py-2 font-medium">conf.</th>
                  <th className="px-3 py-2 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody data-testid="sellers-tbody">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-white/40">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-white/40">
                      No matches
                    </td>
                  </tr>
                )}
                {filtered.map((row) => (
                  <SellerRow
                    key={row.id}
                    row={row}
                    onSave={(patch) => updateInline(row, patch)}
                    onDelete={() => deleteSeller(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bulk replace */}
        <div className="glass-strong rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold mb-2">Bulk replace</h2>
          <p className="text-xs text-white/50 mb-3">
            Paste an array of sellers <code>[{'{ seller_id, name, domain, seller_type, is_confidential }'}, ...]</code> or a full sellers.json object. This <span className="text-red-300">replaces every existing seller</span>.
          </p>
          <textarea
            data-testid="bulk-input"
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            placeholder='[{"seller_id":"mup-1","name":"Example","domain":"example.com","seller_type":"PUBLISHER","is_confidential":0}]'
            rows={6}
            className="admin-input font-mono text-xs resize-y"
          />
          <div className="flex flex-wrap gap-3 mt-3">
            <button
              onClick={bulkReplace}
              disabled={saving || !bulk.trim()}
              data-testid="bulk-replace-btn"
              className="btn-primary text-sm disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Replace all sellers
            </button>
            <button
              onClick={async () => {
                const r = await fetch(liveApiUrl);
                const t = await r.text();
                setBulk(t);
              }}
              className="btn-ghost text-sm"
            >
              <Download className="w-4 h-4" />
              Load current feed
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .admin-input {
          width: 100%;
          padding: 0.55rem 0.75rem;
          border-radius: 0.6rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: #fff;
          font-size: 13px;
        }
        .admin-input:focus {
          outline: none;
          border-color: rgba(0,240,255,0.5);
        }
      `}</style>
    </main>
  );
}

/* ─────────────────────────────────────────────── Small pieces */
function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function PreviewChip({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">
        {label}
      </div>
      <div className="text-white/90 truncate">{value ?? "—"}</div>
    </div>
  );
}


function UrlBox({ label, url, onCopy, testId, primary = false }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        primary
          ? "border-[#00F0FF]/40 bg-[#00F0FF]/[0.04]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div
        className={`text-[10px] uppercase tracking-[0.2em] mb-2 ${
          primary ? "text-[#00F0FF]" : "text-white/40"
        }`}
      >
        {label}
      </div>
      <div className="flex items-center gap-2">
        <code
          data-testid={testId}
          className="text-xs text-[#00F0FF] break-all flex-1"
        >
          {url}
        </code>
        <button
          onClick={onCopy}
          className="p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          aria-label="Copy URL"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function SellerRow({ row, onSave, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [buf, setBuf] = useState(row);

  useEffect(() => { setBuf(row); }, [row]);

  if (!edit) {
    return (
      <tr className="border-b border-white/5 hover:bg-white/[0.02]">
        <td className="px-3 py-2 font-mono text-xs text-[#00F0FF]">{row.seller_id}</td>
        <td className="px-3 py-2">{row.name}</td>
        <td className="px-3 py-2 font-mono text-xs">{row.domain}</td>
        <td className="px-3 py-2 text-xs uppercase tracking-wider text-white/70">{row.seller_type}</td>
        <td className="px-3 py-2 text-xs text-white/60">{row.is_confidential ? "yes" : "no"}</td>
        <td className="px-3 py-2 text-right">
          <div className="inline-flex gap-1">
            <button
              onClick={() => setEdit(true)}
              className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
              aria-label="Edit"
              data-testid={`edit-${row.id}`}
            >
              <Save className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded hover:bg-red-500/20 text-white/60 hover:text-red-300"
              aria-label="Delete"
              data-testid={`delete-${row.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-white/5 bg-white/[0.03]">
      <td className="px-2 py-2">
        <input value={buf.seller_id} onChange={(e) => setBuf({ ...buf, seller_id: e.target.value })} className="admin-input" />
      </td>
      <td className="px-2 py-2">
        <input value={buf.name} onChange={(e) => setBuf({ ...buf, name: e.target.value })} className="admin-input" />
      </td>
      <td className="px-2 py-2">
        <input value={buf.domain} onChange={(e) => setBuf({ ...buf, domain: e.target.value })} className="admin-input" />
      </td>
      <td className="px-2 py-2">
        <select data-testid={`row-type-${row.id}`} value={buf.seller_type} onChange={(e) => setBuf({ ...buf, seller_type: e.target.value })} className="admin-input">
          <option>PUBLISHER</option>
          <option>INTERMEDIARY</option>
          <option>BOTH</option>
        </select>
      </td>
      <td className="px-2 py-2">
        <select
          value={buf.is_confidential}
          onChange={(e) => setBuf({ ...buf, is_confidential: Number(e.target.value) })}
          className="admin-input"
        >
          <option value={0}>0</option>
          <option value={1}>1</option>
        </select>
      </td>
      <td className="px-2 py-2 text-right">
        <div className="inline-flex gap-1">
          <button
            onClick={() => { onSave(buf); setEdit(false); }}
            className="btn-primary text-xs py-1.5 px-2.5"
          >
            Save
          </button>
          <button
            onClick={() => { setBuf(row); setEdit(false); }}
            className="btn-ghost text-xs py-1.5 px-2.5"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}
